import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiCheckBold,
    mdiCloseThick,
    mdiCalendarClock,
    mdiAccountGroup,
    mdiTicketOutline,
    mdiCreditCardOutline,
    mdiEmailOutline,
    mdiChevronDoubleRight,
} from "@mdi/js";

import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import { useMobileMenu } from "../hooks/useMobileMenu";

import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import "./LearnMore.css";
import "./header.css";
import "./Common.css";

const DANCELOCALE_AMOUNTS = [
    { card: "$1.56" },
    { card: "$4.50", bankTransfer: "$0 with bank transfer" },
    { card: "$8.70", bankTransfer: "$0 with bank transfer" },
];

const COMPARISON_VENDORS = [
    {
        name: "Eventbrite",
        detail: "Event ticketing platform.",
        amounts: ["~$3.77", { unsupported: true, text: "Tickets only" }, { unsupported: true, text: "No enrolments" }],
    },
    {
        name: "Humanitix",
        detail: "Event ticketing platform.",
        amounts: ["~$2.19", { unsupported: true, text: "Tickets only" }, { unsupported: true, text: "No enrolments" }],
    },
    {
        name: "Calendly",
        detail: "1:1 appointment scheduler.",
        amounts: [
            { unsupported: true, text: "No ticketing" },
            "$12/mo + Stripe ~3%",
            { unsupported: true, text: "No classes" },
        ],
    },
    {
        name: "Acuity",
        detail: "Squarespace appointment tool.",
        amounts: [
            { unsupported: true, text: "No ticketing" },
            "$20/mo + Stripe ~3%",
            { unsupported: true, text: "No workflow" },
        ],
    },
    {
        name: "DanceStudio-Pro",
        detail: "Dance studio management software.",
        amounts: [
            { unsupported: true, text: "No ticketing" },
            { unsupported: true, text: "Not for 1:1" },
            "$45–$165/mo",
        ],
    },
    {
        name: "Mindbody",
        detail: "Fitness / studio management software.",
        amounts: [
            { unsupported: true, text: "No ticketing" },
            { unsupported: true, text: "Not for 1:1" },
            "$139–$499+/mo",
        ],
    },
];

const TIER_FREE_FEATURES = [
    { included: true, text: "Full ticketing, class management, and instructor privates" },
    { included: true, text: "One service fee on student online payments: 2.5% plus payment processing (typically ~1.7% + 30c for local cards; international cards cost more and are covered in the fee)" },
    { included: true, text: "Branded enrolment and payment pages" },
    { included: true, text: "Bulk email tools" },
    { included: true, text: "Multi-user team access" },
    { included: true, text: "Stripe Connect payouts" },
    { included: true, text: "Bank-transfer option: students pay you directly, with no online service fee" },
];

const LearnMore = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const handleLogout = () => {
        logout();
    };

    useMobileMenu();

    return (
        <div className="pricing-page">
            {/* Header - same structure as LearnMore.js */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <span
                            className="home-text11"
                            style={{ color: "var(--dl-color-orange-default)", cursor: "pointer" }}
                            onClick={() => navigate("/learn-more")}>
                            Learn More
                        </span>
                        <a
                            href="/how-it-works"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <a
                            href="/features"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text12"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <button
                                    onClick={handleLogout}
                                    className="home-text13"
                                    style={{ cursor: "pointer", background: "none", border: "none", color: "inherit" }}>
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text13"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <span
                            className="home-text15"
                            onClick={() => navigate("/learn-more")}
                            style={{ cursor: "pointer", color: "var(--dl-color-orange-default)" }}>
                            Learn More
                        </span>
                        <a
                            href="/how-it-works"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <a
                            href="/features"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <span className="home-text17" onClick={handleLogout} style={{ cursor: "pointer" }}>
                                    Logout
                                </span>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text17"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Hero */}
            <div className="pricing-hero content-card">
                <h1 className="pricing-headline">Free to run your dance business.</h1>
                <p className="pricing-subtext">
                    Ticketing, class enrolments, and instructor privates in one place. No monthly fee. Students pay a service fee on online payments (2.5% plus payment processing).
                </p>
                <p className="pricing-support"><em>Use one tool, all three, or anything in between.</em></p>
                <div className="pricing-hero-cta">
                    <a href="/register" className="home-cta-primary">
                        Start Free
                    </a>
                    <a href="/how-it-works" className="pricing-hero-link">
                        See how it works <Icon path={mdiChevronDoubleRight} size={0.8} />
                    </a>
                </div>
            </div>

            {/* All-in-one product positioning */}
            <div className="pricing-allinone content-card">
                <h2 className="pricing-allinone-heading">Run your dance business in one place</h2>
                <p className="pricing-allinone-subhead">
                    Run classes, instructor privates and events in one place. Works alongside your existing setup, the
                    more payments flow through Dance Localé, the less your studio pays in transaction fees elsewhere.
                </p>
                <div className="pricing-allinone-grid">
                    <div className="pricing-allinone-cell">
                        <div className="pricing-allinone-icon">
                            <Icon path={mdiCalendarClock} size={1.3} />
                        </div>
                        <span className="pricing-allinone-label">Private lesson bookings</span>
                    </div>
                    <div className="pricing-allinone-cell">
                        <div className="pricing-allinone-icon">
                            <Icon path={mdiAccountGroup} size={1.3} />
                        </div>
                        <span className="pricing-allinone-label">Class enrolments</span>
                    </div>
                    <div className="pricing-allinone-cell">
                        <div className="pricing-allinone-icon">
                            <Icon path={mdiTicketOutline} size={1.3} />
                        </div>
                        <span className="pricing-allinone-label">Event ticketing</span>
                    </div>
                    <div className="pricing-allinone-cell">
                        <div className="pricing-allinone-icon">
                            <Icon path={mdiCreditCardOutline} size={1.3} />
                        </div>
                        <span className="pricing-allinone-label">Payments</span>
                    </div>
                    <div className="pricing-allinone-cell">
                        <div className="pricing-allinone-icon">
                            <Icon path={mdiEmailOutline} size={1.3} />
                        </div>
                        <span className="pricing-allinone-label">Communication</span>
                    </div>
                </div>
                <div className="pricing-allinone-features-link-wrap">
                    <a href="/features" className="pricing-allinone-features-link">
                        See the full feature list <Icon path={mdiChevronDoubleRight} size={0.8} />
                    </a>
                </div>
            </div>

            {/* Tiers */}
            <div className="pricing-tiers content-card">
                <h2 className="pricing-tiers-heading">Everything you need is free.</h2>
                <p className="pricing-tiers-subhead">
                    No cost to business. Bank transfer included for students who prefer it.
                </p>

                <div className="pricing-tiers-grid">
                    {/* Free tier */}
                    <div className="pricing-tier-card">
                        <h3 className="pricing-tier-name">Free</h3>
                        <p className="pricing-tier-tagline">For schools that want a simple, no-monthly-fee setup.</p>
                        <div className="pricing-tier-price">$0</div>
                        <p className="pricing-tier-price-detail">
                            No subscriptions. Students pay one service fee on online payments: 2.5% plus standard payment processing.
                        </p>
                        <ul className="pricing-tier-rows">
                            {TIER_FREE_FEATURES.map((feat, idx) => (
                                <li key={idx} className="pricing-tier-row">
                                    <span
                                        className={`pricing-tier-row-icon ${
                                            feat.included ? "pricing-tier-row-icon-yes" : "pricing-tier-row-icon-no"
                                        }`}>
                                        <Icon
                                            path={feat.included ? mdiCheckBold : mdiCloseThick}
                                            size={0.7}
                                            color="var(--dl-color-gray-white)"
                                        />
                                    </span>
                                    <span>{feat.text}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="pricing-tier-cta">
                            <a href="/register" className="home-cta-primary">
                                Start Free
                            </a>
                        </div>
                    </div>

                    {/* Right-column: dual-savings narrative (replaced add-on card 2026-05-10) */}
                    <div className="pricing-savings-card">
                        <h3 className="pricing-savings-heading">Two ways Dance Localé lowers fees.</h3>

                        <div className="pricing-savings-block">
                            <h4 className="pricing-savings-title">For your studio – one fee instead of many</h4>
                            <ul className="pricing-savings-list">
                                <li>Replace multiple platform subscriptions and terminal fees with one service fee paid by students</li>
                                <li>The more sales flow through Dance Localé, the less you pay elsewhere</li>
                                <li>No monthly subscriptions, no setup fees</li>
                            </ul>
                        </div>

                        <div className="pricing-savings-block">
                            <h4 className="pricing-savings-title">For your students, a cheaper way to pay</h4>
                            <ul className="pricing-savings-list">
                                <li>Turn on bank-transfer payments in your settings</li>
                                <li>Students who pay you directly by bank transfer pay no online service fee</li>
                                <li>Paying online stays available for anyone who prefers the convenience</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comparison */}
            <div className="pricing-comparison content-card">
                <h2 className="pricing-comparison-heading">One platform vs many</h2>
                <p className="pricing-comparison-framing">Most platforms only do one job.</p>

                <div className="pricing-comparison-table-wrap">
                    <table className="pricing-comparison-table">
                        <thead>
                            <tr>
                                <th scope="col">Provider</th>
                                <th scope="col" className="pricing-col-amount">
                                    Ticketing
                                    <br />
                                    (e.g. $30 ticket)
                                </th>
                                <th scope="col" className="pricing-col-amount">
                                    Artist booking
                                    <br />
                                    (e.g. $100 lesson)
                                </th>
                                <th scope="col" className="pricing-col-amount">
                                    Class enrolment
                                    <br />
                                    (e.g. $200 course)
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="pricing-row-dancelocale">
                                <th scope="row">
                                    Dance Localé
                                    <span className="pricing-vendor-detail">All three jobs in one platform.</span>
                                </th>
                                {DANCELOCALE_AMOUNTS.map((amount, idx) => (
                                    <td key={idx}>
                                        <div className="pricing-dl-amount">
                                            <div className="pricing-dl-line pricing-dl-line-card">
                                                <span className="pricing-dl-num">{amount.card}</span>
                                            </div>
                                            {amount.bankTransfer && (
                                                <span className="pricing-dl-bank-transfer">{amount.bankTransfer}</span>
                                            )}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                            {COMPARISON_VENDORS.map(vendor => (
                                <tr key={vendor.name}>
                                    <th scope="row">
                                        {vendor.name}
                                        <span className="pricing-vendor-detail">{vendor.detail}</span>
                                    </th>
                                    {vendor.amounts.map((amount, idx) => {
                                        const isUnsupported = typeof amount === "object" && amount.unsupported;
                                        return (
                                            <td
                                                key={idx}
                                                className={
                                                    isUnsupported ? "pricing-cell-unsupported" : "pricing-cell-price"
                                                }>
                                                {isUnsupported ? (
                                                    <span className="pricing-cell-no">
                                                        <span
                                                            className="pricing-cell-no-icon"
                                                            aria-label="Not available">
                                                            <Icon
                                                                path={mdiCloseThick}
                                                                size={0.7}
                                                                color="var(--dl-color-gray-white)"
                                                            />
                                                        </span>
                                                        <span className="pricing-cell-no-caption">{amount.text}</span>
                                                    </span>
                                                ) : (
                                                    amount
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="pricing-comparison-footnote">
                    <span className="pricing-verified-line">Comparison numbers last verified 2026-05-03.</span>
                </p>
            </div>

            {/* Final CTA */}
            <div className="pricing-final-cta">
                <h2 className="pricing-final-heading">Start free. No monthly fees.</h2>
                <div className="pricing-final-cta-row">
                    <a href="/register" className="home-cta-primary">
                        Start Free
                    </a>
                </div>
                <div className="pricing-final-store-banner">
                    <StoreBanner />
                </div>
            </div>

            {/* Footer - same structure as home.js */}
            <div className="home-footer">
                <footer className="home-container22">
                    <div className="home-container23">
                        <span
                            className="home-text56"
                            onClick={() => navigate("/privacy-policy")}
                            style={{ cursor: "pointer" }}>
                            Privacy policy
                        </span>
                        <span
                            className="home-text57"
                            onClick={() => navigate("/terms-conditions")}
                            style={{ cursor: "pointer" }}>
                            Terms of Service
                        </span>
                        <span
                            className="home-text58"
                            onClick={() => navigate("/contact")}
                            style={{ cursor: "pointer" }}>
                            Contact
                        </span>
                    </div>
                    <div className="home-divider"></div>
                    <div className="home-container24">
                        <span className="home-text59 Body2">
                            © {new Date().getFullYear()} Dance Localé. All rights reserved
                        </span>
                        <div className="home-container25">
                            <a
                                href="https://www.instagram.com/dancelocalehq/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link5">
                                <div className="home-container26">
                                    <svg viewBox="0 0 877.7142857142857 1024" className="home-icon25">
                                        <path d="M585.143 512c0-80.571-65.714-146.286-146.286-146.286s-146.286 65.714-146.286 146.286 65.714 146.286 146.286 146.286 146.286-65.714 146.286-146.286zM664 512c0 124.571-100.571 225.143-225.143 225.143s-225.143-100.571-225.143-225.143 100.571-225.143 225.143-225.143 225.143 100.571 225.143 225.143zM725.714 277.714c0 29.143-23.429 52.571-52.571 52.571s-52.571-23.429-52.571-52.571 23.429-52.571 52.571-52.571 52.571 23.429 52.571 52.571zM438.857 152c-64 0-201.143-5.143-258.857 17.714-20 8-34.857 17.714-50.286 33.143s-25.143 30.286-33.143 50.286c-22.857 57.714-17.714 194.857-17.714 258.857s-5.143 201.143 17.714 258.857c8 20 17.714 34.857 33.143 50.286s30.286 25.143 50.286 33.143c57.714 22.857 194.857 17.714 258.857 17.714s201.143 5.143 258.857-17.714c20-8 34.857-17.714 50.286-33.143s25.143-30.286 33.143-50.286c22.857-57.714 17.714-194.857 17.714-258.857s5.143-201.143-17.714-258.857c-8-20-17.714-34.857-33.143-50.286s-30.286-25.143-50.286-33.143c-57.714-22.857-194.857-17.714-258.857-17.714zM877.714 512c0 60.571 0.571 120.571-2.857 181.143-3.429 70.286-19.429 132.571-70.857 184s-113.714 67.429-184 70.857c-60.571 3.429-120.571 2.857-181.143 2.857s-120.571 0.571-181.143-2.857c-70.286-3.429-132.571-19.429-184-70.857s-67.429-113.714-70.857-184c-3.429-60.571-2.857-120.571-2.857-181.143s-0.571-120.571 2.857-181.143c3.429-70.286 19.429-132.571 70.857-184s113.714-67.429 184-70.857c60.571-3.429 120.571-2.857 181.143-2.857s120.571-0.571 181.143 2.857c70.286 3.429 132.571 19.429 184 70.857s67.429 113.714 70.857 184c3.429 60.571 2.857 120.571 2.857 181.143z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://www.facebook.com/DanceLocaleHQ/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link6">
                                <div className="home-container27">
                                    <svg viewBox="0 0 24 24" className="home-icon27">
                                        <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://x.com"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link7">
                                <div className="home-container28">
                                    <svg viewBox="0 0 950.8571428571428 1024" className="home-icon29">
                                        <path d="M925.714 233.143c-25.143 36.571-56.571 69.143-92.571 95.429 0.571 8 0.571 16 0.571 24 0 244-185.714 525.143-525.143 525.143-104.571 0-201.714-30.286-283.429-82.857 14.857 1.714 29.143 2.286 44.571 2.286 86.286 0 165.714-29.143 229.143-78.857-81.143-1.714-149.143-54.857-172.571-128 11.429 1.714 22.857 2.857 34.857 2.857 16.571 0 33.143-2.286 48.571-6.286-84.571-17.143-148-91.429-148-181.143v-2.286c24.571 13.714 53.143 22.286 83.429 23.429-49.714-33.143-82.286-89.714-82.286-153.714 0-34.286 9.143-65.714 25.143-93.143 90.857 112 227.429 185.143 380.571 193.143-2.857-13.714-4.571-28-4.571-42.286 0-101.714 82.286-184.571 184.571-184.571 53.143 0 101.143 22.286 134.857 58.286 41.714-8 81.714-23.429 117.143-44.571-13.714 42.857-42.857 78.857-81.143 101.714 37.143-4 73.143-14.286 106.286-28.571z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="http://www.youtube.com/@DanceLocale"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link8">
                                <div className="home-container29">
                                    <svg viewBox="0 0 1024 1024" className="home-icon31">
                                        <path d="M406.286 644.571l276.571-142.857-276.571-144.571v287.429zM512 152c215.429 0 358.286 10.286 358.286 10.286 20 2.286 64 2.286 102.857 43.429 0 0 31.429 30.857 40.571 101.714 10.857 82.857 10.286 165.714 10.286 165.714v77.714s0.571 82.857-10.286 165.714c-9.143 70.286-40.571 101.714-40.571 101.714-38.857 40.571-82.857 40.571-102.857 42.857 0 0-142.857 10.857-358.286 10.857v0c-266.286-2.286-348-10.286-348-10.286-22.857-4-74.286-2.857-113.143-43.429 0 0-31.429-31.429-40.571-101.714-10.857-82.857-10.286-165.714-10.286-165.714v-77.714s-0.571-82.857-0.571-165.714c9.143-70.857 40.571-101.714 40.571-101.714 38.857-41.143 82.857-41.143 102.857-43.429 0 0 142.857-10.286 358.286-10.286v0z"></path>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default LearnMore;
